import fileDownload from "react-file-download";
import axios from "axios";
import { InvalidCredentialsError } from "../../../domain/errors/invalid-credentials-error";
import { UnexpectedError } from "../../../domain/errors/unexpected-error";
import { FilterModel } from "../../../domain/models/enum";
import {
  Currencyprops,
  Invoice,
  InvoiceModel,
  InvoiceResponseModel,
} from "../../../domain/models/invoice";
import Authentication from "../../../infra/cognito/authentication";
import { HttpClient, HttpStatusCode } from "../../protocols/http/http-client";

export class RemoteInvoice implements Invoice {
  constructor(
    private readonly url: string,
    private readonly httpPostClient: HttpClient,
    private readonly baseUrl: string | undefined
  ) { }

  async load(params?: FilterModel): Promise<InvoiceResponseModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}`,
      method: "get",
      params,
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async create(body: InvoiceModel): Promise<InvoiceModel> {
    const httpResponse = await this.httpPostClient.request({
      url: this.url,
      method: "post",
      body,
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.created:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.conflict:
        throw httpResponse.body;
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async delete(id: number): Promise<InvoiceModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/${id}`,
      method: "delete",
    });
    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.conflict:
        throw httpResponse.body;
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async loadById(id: number): Promise<InvoiceModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/${id}`,
      method: "get",
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async update(
    id: number | undefined,
    body: InvoiceModel
  ): Promise<InvoiceModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/${id}`,
      method: "put",
      body,
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async currency(): Promise<Currencyprops[]> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/currencies`,
      method: "get",
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async loadAll(): Promise<InvoiceModel[]> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/all`,
      method: "get",
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async pdf(id: number | undefined): Promise<InvoiceModel[]> {
    const token = await Authentication.getToken();
    const response = await axios.get(`${this.baseUrl}${this.url}/${id}/export`, {
      method: "get",
      responseType: "blob",
      headers: {
        Authorization: token,
        accept: "*/*",
      },
    });
    return response.data;
  }

  async export(id: number | undefined): Promise<any> {
    const data = await this.pdf(id);
    fileDownload(
      data,
      `Invoice - ${new Date().getTime()}.pdf`
    );
  }
}
